/* How the desktop icons are arranged, and where that choice lives.
 *
 * Two components draw the same grid: the home route, and the layer the shell
 * keeps behind an open window. They have no parent in common that re-renders
 * on navigation, so the choice lives here as a tiny store both subscribe to
 * rather than in either one's state.
 *
 * The snapshot starts at "default" on both server and client so hydration
 * matches, and the saved value is read afterwards, the way every other bit of
 * remembered state on this site is.
 */

const STORAGE_KEY = "desktop-icon-order"

export const ORDERS = [
  { key: "default", label: "Default order" },
  { key: "name", label: "Name" },
  { key: "kind", label: "Kind" },
]

const VALID = new Set(ORDERS.map(o => o.key))

/* Folders before programs before pages, which is roughly how a file manager
   groups things and, more usefully, keeps the four post folders together at
   the top where they were. */
const KIND_RANK = { folder: 0, app: 1, page: 2 }

let current = "default"
const listeners = new Set()

export const subscribe = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const getOrder = () => current

/* Rendered on the server before anything has been read, so it can only ever
   be the authored order. */
export const getServerOrder = () => "default"

const announce = () => listeners.forEach(listener => listener())

export const setOrder = next => {
  if (!VALID.has(next) || next === current) return
  current = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Private mode, or a full quota. The sort still applies for this visit.
  }
  announce()
}

/* Called once the shell is mounted. Anything unrecognised in storage, from an
   older build or a hand-edited value, falls back to the authored order. */
export const restoreOrder = () => {
  let saved = null
  try {
    saved = localStorage.getItem(STORAGE_KEY)
  } catch {
    saved = null
  }

  if (saved && VALID.has(saved) && saved !== current) {
    current = saved
    announce()
  }
}

export const sortIcons = (icons, order) => {
  if (order === "name") {
    return [...icons].sort((a, b) => a.label.localeCompare(b.label))
  }

  if (order === "kind") {
    return [...icons].sort(
      (a, b) =>
        (KIND_RANK[a.kind] ?? 9) - (KIND_RANK[b.kind] ?? 9) ||
        a.label.localeCompare(b.label)
    )
  }

  return icons
}
